/**
 * Shared cookie-consent helpers — used by both `CookieConsentBanner`
 * (the first-visit banner) and `PreferencesPage` (the user-facing
 * "change your mind" surface at /preferences).
 *
 * Storage contract:
 *   - localStorage key: `cookieConsent`
 *   - allowed values:    `'accepted' | 'declined'`
 *   - absence:           the visitor hasn't chosen yet → banner shows
 *
 * GA4 Consent Mode v2 default is set in index.html (everything denied
 * by default with a 500ms wait_for_update window). This module is what
 * actually flips the flags when the visitor makes or changes a choice.
 *
 * Microsoft Clarity (gated): we *load* Clarity only after the visitor
 * accepts. Until they accept, Clarity's script never even downloads —
 * stronger guarantee than Clarity's own consent API which fetches the
 * script and then waits for `clarity('consent')`. Set CLARITY_PROJECT_ID
 * to your real ID from clarity.microsoft.com to activate. While the
 * constant is empty, Clarity is fully inert — safe to ship.
 */

/** Microsoft Clarity project ID. Get yours from clarity.microsoft.com →
 *  create project → Settings → Project ID. While this is empty, Clarity
 *  is fully disabled and no script loads. The real ID activates session
 *  recording + heatmaps (free, consent-gated). */
export const CLARITY_PROJECT_ID = 'wvon1fktql';

/** Leadsy / Instantly visitor identification PID. While empty, Leadsy
 *  is fully disabled. Loads only after Accept on the cookie banner —
 *  matches the GDPR posture we hold every other tracking vendor to. */
export const LEADSY_PID = '16mNOmVG90Q545c3K';

export const CONSENT_STORAGE_KEY = 'cookieConsent';
export type ConsentChoice = 'accepted' | 'declined';

/**
 * Push a Google Consent Mode v2 update to gtag. Safe to call before
 * gtag has finished loading — pushing to dataLayer queues the command
 * for processing once gtag.js is ready.
 */
export const updateGtagConsent = (choice: ConsentChoice) => {
  if (typeof window === 'undefined') return;
  const w = window as unknown as { dataLayer?: unknown[] };
  if (!w.dataLayer) w.dataLayer = [];
  w.dataLayer.push([
    'consent',
    'update',
    {
      analytics_storage: choice === 'accepted' ? 'granted' : 'denied',
      ad_storage: choice === 'accepted' ? 'granted' : 'denied',
      ad_user_data: choice === 'accepted' ? 'granted' : 'denied',
      ad_personalization: choice === 'accepted' ? 'granted' : 'denied',
    },
  ]);
};

/**
 * Load Microsoft Clarity's tracking script. No-op if (a) the project
 * ID is empty (Clarity feature-flagged off), (b) Clarity is already
 * loaded, or (c) we're not in a browser. Called only after the visitor
 * accepts cookies — never before.
 */
export const loadClarity = () => {
  if (!CLARITY_PROJECT_ID) return;
  if (typeof window === 'undefined') return;
  const w = window as unknown as { clarity?: unknown };
  if (w.clarity) return; // already loaded — avoid duplicate script

  // Clarity's official install snippet — adapted to use our constant
  // rather than a literal project ID inline.
  ((c: Window, l: Document, a: string, r: string, i: string) => {
    const cw = c as unknown as Record<string, ((..._args: unknown[]) => void) & { q?: unknown[] }>;
    cw[a] =
      cw[a] ||
      function (...args: unknown[]) {
        (cw[a].q = cw[a].q || []).push(args);
      };
    const t = l.createElement(r) as HTMLScriptElement;
    t.async = true;
    t.src = 'https://www.clarity.ms/tag/' + i;
    const y = l.getElementsByTagName(r)[0];
    y.parentNode?.insertBefore(t, y);
  })(window, document, 'clarity', 'script', CLARITY_PROJECT_ID);
};

/**
 * Load the Leadsy / Instantly visitor-identification tag. Same gating
 * pattern as Clarity — the <script> never enters the DOM until the
 * visitor accepts cookies. Was previously a direct <script> in
 * index.html which (a) set cookies before consent (GDPR risk) and
 * (b) produced CORS errors in the console that hurt Best Practices.
 */
export const loadLeadsy = () => {
  if (!LEADSY_PID) return;
  if (typeof window === 'undefined') return;
  if (document.getElementById('vtag-ai-js')) return; // already loaded

  const s = document.createElement('script');
  s.id = 'vtag-ai-js';
  s.async = true;
  s.src = 'https://r2.leadsy.ai/tag.js';
  s.setAttribute('data-pid', LEADSY_PID);
  s.setAttribute('data-version', '062024');
  document.head.appendChild(s);
};

/**
 * Update every consent-aware vendor based on the visitor's choice.
 * Currently: gtag (GA4), Microsoft Clarity, Leadsy. Add new vendors
 * here — single source of truth for "what fires on Accept / Decline."
 */
export const applyConsent = (choice: ConsentChoice) => {
  updateGtagConsent(choice);
  if (choice === 'accepted') {
    loadClarity();
    loadLeadsy();
  }
  // No teardown on decline — if the visitor later changes to accept,
  // applyConsent('accepted') loads Clarity + Leadsy and re-flips gtag.
  // If they were 'accepted' and switch to 'declined', gtag stops
  // collecting and Clarity stops session recording on its own (its
  // script respects consent updates internally). Leadsy's tag stays
  // resident on the page but stops collecting events once consent
  // flips back to denied.
};

/**
 * On boot, replay the visitor's prior choice so returning visitors
 * don't lose tracking just because the banner already dismissed.
 * Without this, a previously-accepted visitor's first page-view each
 * session would fire under the "denied" default.
 */
export const replayStoredConsent = () => {
  if (typeof window === 'undefined') return;
  const stored = window.localStorage.getItem(
    CONSENT_STORAGE_KEY,
  ) as ConsentChoice | null;
  if (stored === 'accepted' || stored === 'declined') {
    applyConsent(stored);
  }
};

/**
 * Read the current stored choice. Used by the PreferencesPage to render
 * the right active state on the toggle UI.
 */
export const getStoredConsent = (): ConsentChoice | null => {
  if (typeof window === 'undefined') return null;
  const stored = window.localStorage.getItem(
    CONSENT_STORAGE_KEY,
  ) as ConsentChoice | null;
  return stored === 'accepted' || stored === 'declined' ? stored : null;
};

/**
 * Persist the visitor's choice AND apply it to every consent-aware
 * vendor. Single entry point so banner + preferences page can't drift.
 */
export const setStoredConsent = (choice: ConsentChoice) => {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, choice);
  }
  applyConsent(choice);
};
